import { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  Spin,
  Select,
  Input,
  message,
  Modal,
  Tooltip,
  Pagination,
  DatePicker,
  Dropdown,
  Tag,
  Space,
  Upload,
} from "antd";
import {
  CheckCircleTwoTone,
  ClockCircleOutlined,
  CloseCircleTwoTone,
  FilterOutlined,
  MessageOutlined,
  SearchOutlined,
  CalendarOutlined,
  InboxOutlined,
  UserOutlined,
  BankOutlined,
  DollarOutlined,
  FolderOutlined,
  IdcardOutlined,
  TeamOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { RiAddBoxFill } from "react-icons/ri";
import { FcMoneyTransfer } from "react-icons/fc";
import { ColumnType } from "antd/es/table";
import { FaMoneyBillWave, FaPen, FaTrashAlt } from "react-icons/fa";
import { hasPermission } from "../../utils/HasPermission";
import AddReceiptsModel from "./AddReceiptsModel";
import EditReceiptsModel from "./EditReceiptsModel";
import {
  deleteReceipts,
  exportReceiptsExcel,
  getAllReceipts,
  getAllReceiptsForAmounts,
  getCustomerByID,
  getProjectByID,
  getReceiptsLog,
  uploadReceiptsExcel,
} from "../../utils/API";
import { ACCOUNTS, accountName } from "../../utils/accounts";
import dayjs from "dayjs";
import { BiExport, BiMoney } from "react-icons/bi";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

const { Option } = Select;

interface ReceiptsRecord {
  key: string;
  _id: string;
  receipts_number: string;
  project: string;
  customer_name: string;
  amount: number;
  method: string;
  payment_date: string;
  collected_by: string;
  account: number;
  authorize: boolean;
  description: string;
}

const ReceiptsPage = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownsClosed, setDropdownsClosed] = useState(0);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [receiptsModel, setReceiptsModel] = useState(false);
  const [currentTask, setCurrentTask] = useState<ReceiptsRecord | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [filteredMethod, setFilteredMethod] = useState("");
  const [dataSource, setDataSource] = useState<ReceiptsRecord[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const modalScrollRef = useRef<HTMLDivElement>(null);
  const [selectedReceiptsLogs, setSelectedReceiptsLogs] = useState<any[]>([]);
  const [logsPagination, setLogsPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [currentReceiptsLogId, setCurrentReceiptsLogId] = useState<
    string | null
  >(null);
  const [logSearchText, setLogSearchText] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs | null>(null);
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUploadExcel = async (file: File) => {
    setUploading(true);
    try {
      const res: any = await uploadReceiptsExcel(file);
      const imported = res?.data?.imported ?? res?.imported;
      message.success(`${imported ?? 0} receipts imported from Excel`);
      fetchReceipts();
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to import Excel");
    } finally {
      setUploading(false);
    }
    return false; // prevent antd auto-upload
  };
  const [filteredProject, setFilteredProject] = useState("");
  const [filteredAccount, setFilteredAccount] = useState<any>(undefined);
  const [activeFilter, setActiveFilter] = useState<"method" | "project" | null>(
    null,
  );
  const accounts = ACCOUNTS;

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownsClosed((prev) => prev + 1);
  };

  const resetSettings = () => {
    // setSelectedSource([]);
    // setTimeFilter(undefined);
    // setStartDate(null);
    // setEndDate(null);
    // setTempStartDate(null);
    // setShowRangePicker(false);
    // setSelectedStatus([]);
    // setFavName("");
    // setError("");
  };

  const editReceipts = (record: ReceiptsRecord) => {
    setCurrentTask(record);
    setIsEditModalVisible(true);
    fetchReceipts();
  };

  const TwoLineClampWithTooltip = ({ text }: { text: string }) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = textRef.current;
      if (!el) return;

      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const maxHeight = lineHeight * 2;

      // Detect overflow
      if (el.scrollHeight > maxHeight + 1) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }, [text]);

    const content = (
      <div
        ref={textRef}
        className="line-clamp-2 text-gray-800 cursor-pointer"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );

    return isOverflowing ? (
      <Tooltip
        title={
          <div
            className="max-h-[250px] overflow-y-auto p-2"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        }
        placement="top"
        color="#CCCCCC"
        overlayInnerStyle={{
          color: "#000",
          padding: "12px",
          maxWidth: "500px",
          minWidth: "400px",
          maxHeight: "400px",
        }}
      >
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      // Backend now populates customer_name/project and sorts by payment_date,
      // so we use those directly (no per-row N+1 fetches).
      const response = await getAllReceiptsForAmounts(1, true);

      if (response?.data && Array.isArray(response.data)) {
        // Sort by payment_date (latest first); createdAt fallback. payment_date is
        // stored as DD-MM-YYYY so a DB string sort is lexical, not chronological.
        const enrichedData = response.data
          .sort((a: any, b: any) => {
            const dateA = getTimestampFromDate(a.payment_date);
            const dateB = getTimestampFromDate(b.payment_date);
            if (dateA > 0 && dateB > 0) {
              return dateB - dateA;
            }
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          })
          .map((receipt: any, index: number) => ({
            ...receipt,
            serialNumber: index + 1,
            customer_name:
              typeof receipt.customer_name === "object"
                ? receipt.customer_name?.name
                : receipt.customer_name,
            project:
              typeof receipt.project === "object"
                ? receipt.project?.projectName
                : receipt.project,
          }));

        setDataSource(enrichedData);
        setTotalRecords(enrichedData.length);
      } else {
        setDataSource([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching receipts: ", error);
      message.error("Error fetching receipts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response: any = await deleteReceipts(deleteId);
        // console.log("Delete Response:", response); // Debugging log
        fetchReceipts(); // Refresh list

        if (response?.success) {
          message.success("Receipts deleted successfully");
        } else {
          // message.error(response?.message || "Failed to delete receipts");
        }
      } catch (error) {
        console.error("Delete Error:", error);
        message.error("Error deleting receipts. Please try again.");
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handleViewLeadLogs = async (
    receiptId: string,
    page = 1,
    search = "",
  ) => {
    try {
      setCurrentReceiptsLogId(receiptId);
      setIsLogModalOpen(true);
      setLogLoading(true);
      let type = "Receipts";

      const data = await getReceiptsLog(
        receiptId,
        page,
        logsPagination.pageSize,
        search,
        type,
      );

      console.log("log:", data);

      setSelectedReceiptsLogs(data.logs);
      setLogsPagination((prev) => ({
        ...prev,
        current: page,
        total: data.pagination.total,
        pageSize: data.pagination.pageSize,
      }));

      // scroll to top of modal
      setTimeout(() => {
        modalScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } catch (error) {
      console.error("Failed to fetch receipts logs:", error);
    } finally {
      setLogLoading(false);
    }
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format("DD MMM YYYY");
  };

  // Function to format amount
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get method color
  const getMethodColor = (method: string) => {
    switch (method?.toLowerCase()) {
      case "cash":
        return "green";
      case "bank":
        return "blue";
      default:
        return "default";
    }
  };

  // Columns for the Ant Design Table - With proper layout for customer and project
  const columns: ColumnType<ReceiptsRecord>[] = [
    {
      title: "#",
      key: "index",
      fixed: "left" as const,
      width: 60,
      align: "center" as const,
      render: (_: any, __: any, index: number) => (
        <span style={{ fontWeight: 500, color: "#4B5563" }}>
          {(page - 1) * 10 + index + 1}
        </span>
      ),
    },
    {
      title: "Payment Date",
      key: "payment_date",
      dataIndex: "payment_date",
    },
    {
      title: "Account",
      key: "account",
      dataIndex: "account",
      render: (value: any) => accountName(value) || "N/A",
    },
    {
      title: "Receipt No.",
      dataIndex: "receipts_number",
      key: "receipts_number",
      width: 150,
      render: (text: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {/* <IdcardOutlined style={{ color: "#6B7280", fontSize: "14px" }} /> */}
          <span
            style={{
              fontWeight: 600,
              fontSize: "13px",
              color: "#1F2937",
              letterSpacing: "0.3px",
            }}
          >
            {text || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 180,
      render: (text?: string) => {
        if (!text) return <Tag color="default">N/A</Tag>;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#F3F4F6",
              padding: "4px 8px",
              borderRadius: "6px",
              width: "fit-content",
            }}
          >
            <FolderOutlined style={{ color: "#3B82F6", fontSize: "16px" }} />
            <span
              style={{
                fontWeight: 500,
                color: "#1F2937",
              }}
            >
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      width: 150,
      render: (text?: string) => {
        if (!text) return <Tag color="default">N/A</Tag>;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#F3F4F6",
              padding: "4px 8px",
              borderRadius: "6px",
              width: "fit-content",
            }}
          >
            <TeamOutlined style={{ color: "#10B981", fontSize: "16px" }} />
            <span
              style={{
                fontWeight: 500,
                color: "#1F2937",
              }}
            >
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right" as const,
      render: (text: number) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: "#059669",
              fontFamily: "monospace",
            }}
          >
            {" "}
            {formatAmount(text)}
          </span>
        </div>
      ),
      sorter: (a: ReceiptsRecord, b: ReceiptsRecord) => a.amount - b.amount,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      width: 100,
      render: (text: string) => {
        const isCash = text?.toLowerCase() === "cash";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {/* {isCash ? (
                            <FaMoneyBillWave style={{ color: "#10b981", fontSize: "16px" }} />
                        ) : (
                            <BankOutlined style={{ color: "#2563eb", fontSize: "14px" }} />
                        )} */}
            <Tag color={getMethodColor(text)} style={{ fontWeight: 500 }}>
              {text || "N/A"}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Description",
      key: "description",
      dataIndex: "description",
      width: 200,
      render: (text?: string) => {
        if (!text) return <Tag color="default">N/A</Tag>;
        return <TwoLineClampWithTooltip text={text} />;
      },
    },
    {
      title: "Collected By",
      key: "collected_by",
      dataIndex: "collected_by",
      width: 120,
      render: (text?: string) => {
        if (!text) return <Tag color="default">N/A</Tag>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <UserOutlined style={{ color: "#6B7280", fontSize: "14px" }} />
            <span style={{ fontWeight: 500 }}>{text}</span>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "authorize",
      key: "authorize",
      width: 100,
      align: "center" as const,
      render: (value: boolean) => (
        <div style={{ textAlign: "center" }}>
          {value ? (
            <Tag
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              color="success"
              style={{ padding: "2px 8px" }}
            >
              Authorized
            </Tag>
          ) : (
            <Tag
              icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}
              color="error"
              style={{ padding: "2px 8px" }}
            >
              Pending
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Logs",
      key: "receiptsLog",
      width: 80,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Tooltip title="View Logs">
          <button
            onClick={(e) => {
              e.preventDefault();
              handleViewLeadLogs(record._id);
            }}
            className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-blue-600 shadow-[0_4px_12px_rgba(96,165,250,0.3)] hover:shadow-[0_6px_16px_rgba(96,165,250,0.4)] active:translate-y-1 active:shadow-none text-white flex items-center justify-center transition-all duration-200 group"
          >
            <ProfileOutlined style={{ fontSize: "14px" }} />
          </button>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      align: "center" as const,
      render: (_: any, record: ReceiptsRecord) => (
        <div className="flex items-center justify-center gap-1">
          <Tooltip title="Edit">
            <button
              onClick={() => {
                closeAllDropdowns();
                editReceipts(record);
              }}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-100 bg-white text-slate-500 shadow-sm transition-all hover:border-slate-300 hover:text-slate-700"
            >
              <FaPen size={13} />
            </button>
          </Tooltip>

          {hasPermission("customers", "delete") && (
            <Tooltip title="Delete">
              <button
                onClick={() => {
                  closeAllDropdowns();
                  setDeleteId(record._id);
                }}
                className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-100 bg-white text-rose-500 shadow-sm transition-all hover:border-rose-200 hover:bg-rose-50"
              >
                <FaTrashAlt size={13} />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  // ponytail: payment_date is stored as DD-MM-YYYY; parse it like the Expense page does.
  const getTimestampFromDate = (dateStr: string) => {
    if (!dateStr) return 0;
    const parsed = dayjs(dateStr, ["DD-MM-YYYY", "D-M-YYYY", "YYYY-MM-DD"], true);
    return parsed.isValid() ? parsed.valueOf() : 0;
  };

  // Filtered data by search text
  const projectOptions = Array.from(
    new Set(dataSource.map((item) => item.project).filter(Boolean)),
  );

  const filteredData = dataSource.filter((item) => {
    const searchData =
      (item.customer_name?.toString().toLowerCase() || "").includes(
        searchText.toString().toLowerCase(),
      ) ||
      (item.receipts_number?.toString().toLowerCase() || "").includes(
        searchText.toString().toLowerCase(),
      ) ||
      (item.project?.toString().toLowerCase() || "").includes(
        searchText.toString().toLowerCase(),
      ) ||
      (item.description?.toString().toLowerCase() || "").includes(
        searchText.toString().toLowerCase(),
      );

    const filteredMethodData = filteredMethod
      ? item.method === filteredMethod
      : true;
    const filteredProjectData = filteredProject
      ? item.project === filteredProject
      : true;
    const filteredAccountData = filteredAccount !== undefined
      ? Number(item.account) === Number(filteredAccount)
      : true;

    const filteredMonthData = selectedMonth
      ? (() => {
          const startOfMonth = selectedMonth.startOf("month").valueOf();
          const endOfMonth = selectedMonth.endOf("month").valueOf();
          const itemTimestamp = getTimestampFromDate(item.payment_date);
          return itemTimestamp >= startOfMonth && itemTimestamp <= endOfMonth;
        })()
      : true;

    const filteredFromDate = fromDate
      ? (() => {
          const start = fromDate.startOf("day").valueOf();
          return getTimestampFromDate(item.payment_date) >= start;
        })()
      : true;
    const filteredToDate = toDate
      ? (() => {
          const end = toDate.endOf("day").valueOf();
          return getTimestampFromDate(item.payment_date) <= end;
        })()
      : true;

    return (
      searchData &&
      filteredMethodData &&
      filteredProjectData &&
      filteredAccountData &&
      filteredMonthData &&
      filteredFromDate &&
      filteredToDate
    );
  });

  // Calculate total amount
  const totalAmount = filteredData.reduce(
    (acc, item) => acc + (Number(item.amount) || 0),
    0,
  );

  const pageSize = 10; // records per page

  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  useEffect(() => {
    fetchReceipts();
  }, [page]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params: any = {};

      if (selectedMonth) {
        params.month = selectedMonth.format("YYYY-MM");
      }

      if (filteredMethod) {
        params.method = filteredMethod;
      }

      if (filteredAccount !== undefined) {
        params.account = filteredAccount;
      }

      if (fromDate) {
        params.fromDate = fromDate.format("DD-MM-YYYY");
      }

      if (toDate) {
        params.toDate = toDate.format("DD-MM-YYYY");
      }

      if (searchText) {
        params.search = searchText;
      }

      const response = await exportReceiptsExcel(params);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      const today = new Date();

      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();

      const formattedDate = `${day}_${month}_${year}`;

      link.download = `Receipts_${formattedDate}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success("Excel exported successfully");
    } catch (error) {
      message.error("Failed to export Excel");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const filterMenu = (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "12px",
        width: 240,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Filters</h4>

      {/* Method Filter */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Method</div>
        <Select
          placeholder="Select Method"
          value={filteredMethod || undefined}
          allowClear
          style={{ width: "100%" }}
          onChange={(value) => {
            setFilteredMethod(value || "");
            setPage(1);
          }}
          options={[
            { value: "Bank", label: "Bank" },
            { value: "Cash", label: "Cash" },
          ]}
        />
      </div>

      {/* Project Filter */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Project</div>
        <Select
          placeholder="Select Project"
          value={filteredProject || undefined}
          allowClear
          style={{ width: "100%" }}
          onChange={(value) => {
            setFilteredProject(value || "");
            setPage(1);
          }}
        >
          {projectOptions.map((project) => (
            <Option key={project} value={project}>
              {project}
            </Option>
          ))}
        </Select>
      </div>

      {/* Account Filter */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Account</div>
        <Select
          placeholder="Select Account"
          value={filteredAccount}
          allowClear
          style={{ width: "100%" }}
          onChange={(value) => {
            setFilteredAccount(value);
            setPage(1);
          }}
        >
          {accounts.map((acc: any) => (
            <Option key={acc.value} value={acc.value}>
              {acc.name}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* Header Section - Responsive with consistent spacing */}
      <div className="m-2 mb-4">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Left side - Title and Total Amount */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 600,
                margin: 0,
                whiteSpace: "nowrap",
                color: "#111827",
              }}
            >
              Receipts
            </h2>

            <div
              style={{
                background: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                padding: "4px 12px",
                display: "flex",
                height: "70px",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FcMoneyTransfer style={{ fontSize: "30px" }} />
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                Total Amount:
              </span>
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#059669",
                  whiteSpace: "nowrap",
                }}
              >
                {formatAmount(totalAmount)}
              </span>
            </div>
          </div>

          {/* Right side - All Controls */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              flex: "1 1 auto",
              justifyContent: "flex-end",
            }}
          >
            {/* Month Picker */}
            <DatePicker
              picker="month"
              placeholder="Select Month"
              format="MMM YYYY"
              value={selectedMonth}
              onChange={(date) => {
                setSelectedMonth(date);
                if (date) {
                  setFromDate(null);
                  setToDate(null);
                }
                setPage(1);
              }}
              allowClear
              style={{
                width: "140px",
                height: "40px",
                borderRadius: "8px",
              }}
              size="middle"
            />

            {/* Custom Date Range */}
            <DatePicker.RangePicker
              format="DD-MM-YYYY"
              placeholder={["From Date", "To Date"]}
              value={[fromDate, toDate]}
              onChange={(dates) => {
                setFromDate(dates ? dates[0] : null);
                setToDate(dates ? dates[1] : null);
                if (dates) setSelectedMonth(null);
                setPage(1);
              }}
              allowClear
              style={{
                height: "40px",
                borderRadius: "8px",
              }}
              size="middle"
            />

            {/* Search with Filter Button */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                placeholder="Search receipts..."
                prefix={<SearchOutlined style={{ color: "#9CA3AF" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{
                  width: "220px",
                  height: "40px",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: "none",
                }}
                size="middle"
              />
              <Dropdown
                dropdownRender={() => filterMenu}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  style={{
                    height: "40px",
                    width: "42px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "none",
                    padding: 0,
                    backgroundColor: "#fff",
                  }}
                  size="middle"
                >
                  <FilterOutlined style={{ color: "#4B5563" }} />
                </Button>
              </Dropdown>
            </div>

            {/* Add Button */}
            <Button
              type="primary"
              onClick={() => {
                closeAllDropdowns();
                setIsModelOpen(true);
              }}
              size="middle"
              style={{
                background: "#001529",
                height: "40px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "0 16px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "8px",
              }}
            >
              <RiAddBoxFill style={{ fontSize: "18px" }} />
              Add
            </Button>

            {/* Upload Bank Statement Excel */}
            <Upload
              accept=".xlsx,.xls,.csv"
              showUploadList={false}
              beforeUpload={handleUploadExcel}
            >
              <Button
                loading={uploading}
                size="middle"
                style={{
                  background: "#001529",
                  height: "40px",
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "8px",
                  color: "#fff",
                }}
              >
                <InboxOutlined />
                Upload Excel
              </Button>
            </Upload>

            {/* Export Button */}
            <Tooltip title="Export to Excel" color="#CCCCCC">
              <Button
                type="primary"
                onClick={handleExportExcel}
                loading={exporting}
                size="middle"
                style={{
                  background: "#001529",
                  height: "40px",
                  width: "40px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                }}
              >
                {!exporting && <BiExport size={18} />}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Add Receipts Modal */}
      <AddReceiptsModel
        visible={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        onTaskCreated={() => {
          setIsModelOpen(false);
          fetchReceipts();
        }}
      />

      {/* Receipts Log Modal */}
      <Modal
        title={
          <div className="flex justify-between items-center w-full">
            <span className="text-xl font-bold">Receipts Log</span>
            <Input
              placeholder="ðŸ” Search logs..."
              allowClear
              value={logSearchText}
              onChange={(e) => {
                setLogSearchText(e.target.value);
                if (currentReceiptsLogId) {
                  handleViewLeadLogs(currentReceiptsLogId, 1, e.target.value);
                }
              }}
              className="w-64 rounded text-sm mr-10"
            />
          </div>
        }
        open={isLogModalOpen}
        onCancel={() => setIsLogModalOpen(false)}
        footer={null}
        width={650}
        style={{
          bottom: 20,
          top: 20,
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        bodyStyle={{
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
      >
        <Spin spinning={logLoading}>
          <div ref={modalScrollRef} className="">
            <div className="elements">
              <table className="w-full bg-white shadow-md rounded-lg">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border p-3 text-left w-1/3">
                      <ClockCircleOutlined className="mr-2" /> Updated At
                    </th>
                    <th className="border p-3 text-left">
                      <MessageOutlined className="mr-2" /> Message
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {selectedReceiptsLogs.length > 0 ? (
                    selectedReceiptsLogs.map((log) => {
                      const formattedTimestamp = dayjs(log.createdAt)
                        .utc()
                        .local()
                        .format("DD-MM-YYYY HH:mm");

                      // Check if message contains HTML tags
                      const hasHTML = /<[^>]*>/.test(log.message);

                      if (hasHTML) {
                        // For HTML content (like your description field)
                        const plainText = log.message
                          .replace(/<[^>]*>/g, " ")
                          .replace(/\s+/g, " ")
                          .trim();
                        const isLong = plainText.length > 110;
                        const displayText = isLong
                          ? plainText.substring(0, 110) + "..."
                          : plainText;

                        return (
                          <tr
                            key={log._id}
                            className="hover:bg-gray-100 transition"
                          >
                            <td className="border p-3">{formattedTimestamp}</td>
                            <td className="border p-3 max-w-xl align-top">
                              {isLong ? (
                                <Tooltip
                                  title={
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: log.message,
                                      }}
                                      className="description-tooltip-content"
                                    />
                                  }
                                  classNames={{ root: "plain-tooltip" }}
                                  mouseEnterDelay={0.3}
                                  placement="top"
                                  overlayStyle={{
                                    maxWidth: "500px",
                                    maxHeight: "400px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <span style={{ cursor: "pointer" }}>
                                    {log.message}
                                  </span>
                                </Tooltip>
                              ) : (
                                <span style={{ cursor: "pointer" }}>
                                  {log.message}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      } else {
                        // For plain text content
                        const isLong = log.message.length > 110;
                        const displayText = isLong
                          ? log.message.substring(0, 110) + "..."
                          : log.message;

                        return (
                          <tr
                            key={log._id}
                            className="hover:bg-gray-100 transition"
                          >
                            <td className="border p-3">{formattedTimestamp}</td>
                            <td className="border p-3 max-w-xl">
                              {isLong ? (
                                <Tooltip
                                  title={log.message}
                                  placement="topLeft"
                                  color="#CCCCCC"
                                  overlayInnerStyle={{
                                    color: "#000",
                                    padding: "12px",
                                    maxWidth: "500px",
                                    minWidth: "400px",
                                    maxHeight: "400px",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "-webkit-box",
                                      WebkitBoxOrient: "vertical",
                                      WebkitLineClamp: 2,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      maxWidth: "350px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {displayText}
                                  </span>
                                </Tooltip>
                              ) : (
                                <span
                                  style={{
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 2,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    maxWidth: "350px",
                                    cursor: "pointer",
                                  }}
                                >
                                  {displayText}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={2}
                        className="border p-3 text-center text-gray-500"
                      >
                        No logs available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="footer-modals" />
            <div className="p-3">
              <div className="d-flex justify-content-end">
                <Pagination
                  current={logsPagination.current}
                  pageSize={logsPagination.pageSize}
                  total={logsPagination.total}
                  onChange={(page) => {
                    if (currentReceiptsLogId) {
                      handleViewLeadLogs(
                        currentReceiptsLogId,
                        page,
                        logSearchText,
                      );
                    }
                  }}
                  showSizeChanger={false}
                />
              </div>
            </div>
          </div>
        </Spin>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Receipts?</p>
      </Modal>

      <Card
        className="mt-4"
        style={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Spin spinning={loading}>
          <div
            className="scrollbar-hide custom-scroll-table"
            style={{ overflowX: "auto" }}
          >
            <Table
              scroll={{ x: "max-content" }}
              columns={columns}
              dataSource={paginatedData}
              loading={loading}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                current: page,
                onChange: (pageNumber) => setPage(pageNumber),
                showSizeChanger: false,
                total: filteredData.length,
              }}
              //size="middle"
              bordered={false}
              className="receipts-table"
              rowClassName="hover:bg-gray-50"
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
              }}
            />
          </div>
        </Spin>
      </Card>

      {currentTask && (
        <EditReceiptsModel
          visible={isEditModalVisible}
          id={currentTask?._id}
          onClose={() => setIsEditModalVisible(false)}
          onTaskCreated={function (): void {
            setIsModelOpen(false);
          }}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchReceipts();
          }}
        />
      )}
    </div>
  );
};

export const RECEIPTS_TABLE_COLUMNS = [
  "payment_date",
  "receipts_number",
  "project",
  "customer_name",
  "amount",
  "method",
  "description",
  "collected_by",
  "authorize",
];

export default ReceiptsPage;

















